import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Calendar, Play, Globe, Shield, BookOpen, Cloud, Wind, Droplets, Newspaper, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ImpactTracker from "@/components/ImpactTracker";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1655720359248-eeace8c709c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwbXVsdGlyYWNpYWwlMjBoYW5kcyUyMHN0YWNrZWQlMjBjaXJjbGUlMjB1bml0eXxlbnwwfHx8fDE3NzAzNDc0OTh8MA&ixlib=rb-4.1.0&q=85";
const VOLUNTEER_IMAGE = "https://images.unsplash.com/photo-1560220604-1985ebfe28b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxkaXZlcnNlJTIwY29tbXVuaXR5JTIwdm9sdW50ZWVycyUyMGdyb3VwfGVufDB8fHx8MTc3MDAwODU4OHww&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: Globe,
    title: "Community Peacebuilding",
    description: "Empowering local communities to resolve conflicts and build lasting peace."
  },
  {
    icon: Shield,
    title: "Violence Prevention",
    description: "Programs designed to address root causes and prevent violence before it starts."
  },
  {
    icon: BookOpen,
    title: "Peace Education",
    description: "Teaching conflict resolution and peace principles in schools and communities."
  }
];

const stats = [
  { number: "5,000+", label: "Lives Impacted" },
  { number: "150+", label: "Volunteers" },
  { number: "50+", label: "Community Events" },
  { number: "10+", label: "Years of Service" }
];

export default function Home() {
  const [videos, setVideos] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [weather, setWeather] = useState(null);
  const [news, setNews] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [videosRes, eventsRes, weatherRes, newsRes] = await Promise.all([
          axios.get(`${API}/videos`),
          axios.get(`${API}/events`),
          axios.get(`${API}/weather`),
          axios.get(`${API}/news`)
        ]);
        setVideos(videosRes.data.slice(0, 3));
        setEvents(eventsRes.data.slice(0, 3));
        setWeather(weatherRes.data);
        setNews(newsRes.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInUp">
              <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Building a Culture of Peace
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] text-[#0F172A] leading-tight mb-6">
                Educate. Advocate. <span className="text-[#0056D2]">Transform.</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
                Peace for Change Alliance Inc. is dedicated to mobilizing communities toward a culture of peace through education, advocacy, and transformative action.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/donate">
                  <Button className="btn-donate" data-testid="hero-donate-btn">
                    <Heart className="w-5 h-5 mr-2" />
                    Donate Now
                  </Button>
                </Link>
                <Link to="/volunteer">
                  <Button variant="outline" className="btn-secondary" data-testid="hero-volunteer-btn">
                    Join Our Mission
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="animate-fadeInUp delay-200">
              <div className="relative">
                <img
                  src={HERO_IMAGE}
                  alt="Helping hands representing care and peace"
                  className="rounded-3xl shadow-2xl w-full h-[400px] lg:h-[500px] object-cover"
                />
                {/* Weather Widget */}
                {weather && weather.temperature && (
                  <div className="absolute -top-4 -right-4 bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-lg hidden lg:block border border-slate-100">
                    <div className="flex items-center gap-3">
                      <div className="text-4xl">{weather.icon}</div>
                      <div>
                        <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                          <MapPin className="w-3 h-3" />
                          <span>{weather.location}</span>
                        </div>
                        <p className="text-3xl font-bold text-[#0F172A]">{weather.temperature}°F</p>
                        <p className="text-sm text-slate-600">{weather.description}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Wind className="w-3 h-3" /> {weather.wind_speed} mph
                          </span>
                          <span className="flex items-center gap-1">
                            <Droplets className="w-3 h-3" /> {weather.humidity}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-lg hidden lg:block">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0056D2] rounded-full flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#0F172A]">150+</p>
                      <p className="text-sm text-slate-500">Active Volunteers</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile Weather Widget */}
          {weather && weather.temperature && (
            <div className="mt-8 lg:hidden bg-gradient-to-r from-[#0056D2] to-[#0077FF] rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{weather.icon}</div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-white/80 mb-1">
                      <MapPin className="w-3 h-3" />
                      <span>{weather.location}</span>
                    </div>
                    <p className="text-3xl font-bold text-white">{weather.temperature}°F</p>
                    <p className="text-sm text-white/90">{weather.description}</p>
                  </div>
                </div>
                <div className="text-right text-white/80 text-xs">
                  <div className="flex items-center gap-1 justify-end mb-1">
                    <Wind className="w-3 h-3" /> {weather.wind_speed} mph
                  </div>
                  <div className="flex items-center gap-1 justify-end">
                    <Droplets className="w-3 h-3" /> {weather.humidity}%
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ImpactTracker />
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">Our Mission</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-4 mb-6">
              Creating Lasting Peace Through Action
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              We believe in the power of education, advocacy, and community mobilization to transform systems and public policy toward a culture of peace.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="card-feature group">
                <div className="w-14 h-14 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#0056D2] transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-[#0056D2] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-[#0F172A] mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Videos Section */}
      <section className="py-20 bg-[#F0F9FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
            <div>
              <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">Featured Videos</span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-2">
                Voices Against Violence
              </h2>
            </div>
            <Link to="/videos" className="mt-4 md:mt-0">
              <Button variant="outline" className="btn-secondary">
                View All Videos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {videos.map((video) => (
                <div 
                  key={video.id} 
                  className="card-video"
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`video-card-${video.id}`}
                >
                  <div className="relative aspect-video bg-slate-200">
                    <img 
                      src={video.thumbnail_url} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/640x360?text=Video+Thumbnail";
                      }}
                    />
                    <div className="video-overlay">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#0056D2] ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-[#0F172A] mb-2 line-clamp-2">{video.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-2">{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-2xl">
              <Play className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No videos available yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={VOLUNTEER_IMAGE}
                alt="Volunteers working together"
                className="rounded-3xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
            <div>
              <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">Get Involved</span>
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-4 mb-6">
                Be Part of the Change
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                Join our community of dedicated volunteers making a difference. Whether you can give your time, skills, or resources, every contribution helps build a more peaceful world.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/volunteer">
                  <Button className="btn-primary" data-testid="cta-volunteer-btn">
                    <Users className="w-5 h-5 mr-2" />
                    Become a Volunteer
                  </Button>
                </Link>
                <Link to="/events">
                  <Button variant="outline" className="btn-secondary" data-testid="cta-events-btn">
                    <Calendar className="w-5 h-5 mr-2" />
                    View Events
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events Section */}
      {events.length > 0 && (
        <section className="py-20 bg-[#F0F9FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
              <div>
                <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">Upcoming Events</span>
                <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-2">
                  Join Us
                </h2>
              </div>
              <Link to="/events" className="mt-4 md:mt-0">
                <Button variant="outline" className="btn-secondary">
                  View All Events
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {events.map((event) => {
                const eventDate = new Date(event.event_date);
                const month = eventDate.toLocaleDateString('en-US', { month: 'short' });
                const day = eventDate.getDate();
                
                return (
                  <div key={event.id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
                    {event.image_url && (
                      <img 
                        src={event.image_url} 
                        alt={event.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-6">
                      <div className="flex gap-4 mb-4">
                        <div className="date-badge">
                          <p className="date-badge-month">{month}</p>
                          <p className="date-badge-day">{day}</p>
                        </div>
                        <div>
                          <h3 className="font-semibold text-[#0F172A] mb-1">{event.title}</h3>
                          <p className="text-sm text-slate-500">{event.event_time}</p>
                        </div>
                      </div>
                      <p className="text-sm text-slate-600 line-clamp-2">{event.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* News Section */}
      {news.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12">
              <div>
                <span className="text-[#0056D2] font-semibold text-sm uppercase tracking-wider">Milwaukee News</span>
                <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0F172A] mt-2">
                  Local News & Updates
                </h2>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {news.map((item) => {
                const isExternal = item.url?.startsWith('http');
                const NewsCard = (
                  <div className="bg-slate-50 rounded-2xl overflow-hidden hover:bg-slate-100 transition-colors group h-full flex flex-col">
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-40 object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-3 py-1 rounded-full text-xs font-semibold">
                          {item.category}
                        </span>
                        {isExternal && (
                          <Newspaper className="w-3 h-3 text-slate-400" />
                        )}
                      </div>
                      <h3 className="font-semibold text-[#0F172A] mb-2 group-hover:text-[#0056D2] transition-colors line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-3 mb-4 flex-1">
                        {item.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>{item.source}</span>
                        <span>{item.date}</span>
                      </div>
                    </div>
                  </div>
                );

                return isExternal ? (
                  <a 
                    key={item.id} 
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {NewsCard}
                  </a>
                ) : (
                  <Link key={item.id} to={item.url} className="block">
                    {NewsCard}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Video Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0">
          <DialogTitle className="sr-only">{selectedVideo?.title}</DialogTitle>
          {selectedVideo && (
            <div className="youtube-container">
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=1`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
